import React, { useState, useEffect, useCallback } from 'react';
import { baseUrl, envVar } from '../../../config/baseUrl';
import { errerMessages } from '../../../helpers/error-messages-enum';
import { APPLE_PAY_ELIGIBLE_STATUSES, payment_providers_names } from '../../../utils/enums';
import { deviceInfo, threeDsRedirect } from '../../../helpers';
import axios from 'axios';

// via = btoa('frontend-crst-apay')
const VIA_PARAM = 'ZnJvbnRlbmQtY3JzdC1hcGF5';

export default function ApplePayCS({ encryptData, base64Data, decodedString, setError, setMessage, setFailedMessage, setLoading }) {
  const [isEnabled, setIsEnabled] = useState(false);
  const [supportedNetworks, setSupportedNetworks] = useState(
    envVar.aPaySupportedCardNetworks?.split(',') || ['visa', 'masterCard', 'amex', 'discover']
  );

  const ApplePaySession = window.ApplePaySession;

  useEffect(() => {
    const checkPaymentEligibility = async () => {
      try {
        const capabilities = await window.ApplePaySession.applePayCapabilities(envVar.aPayCsMerchantIdentifier);
        const isEligible = APPLE_PAY_ELIGIBLE_STATUSES.includes(capabilities?.paymentCredentialStatus);
        isEligible && setIsEnabled(true);
      } catch (error) {
        console.log('ApplePayCS eligibility check error', error?.message);
      }
    };

    // The Apple Pay JS SDK can load asynchronously/late, so poll for it to
    // become available (100ms interval, ~10s timeout) instead of checking once.
    if (window.ApplePaySession) {
      checkPaymentEligibility();
      return;
    }
    const intervalId = setInterval(() => {
      if (window.ApplePaySession) {
        clearInterval(intervalId);
        checkPaymentEligibility();
      }
    }, 100);
    const timeoutId = setTimeout(() => clearInterval(intervalId), 10000);
    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    let cardList = envVar.aPaySupportedCardNetworks?.split(',') || ['visa', 'masterCard', 'amex', 'discover'];
    if (decodedString?.exclude_card_networks) {
      const excludeList = decodedString.exclude_card_networks.split(',');
      cardList = cardList.filter(card => !excludeList.includes(card));
    }
    setSupportedNetworks(cardList);
  }, [decodedString]);

  const validateMerchant = async (validationUrl) => {
    return axios.post(`${baseUrl.switch}/validate-session`, {
      appleUrl: validationUrl,
      merchantIdentifier: envVar.aPayCsMerchantIdentifier,
      domainName: envVar.aPayDomainName,
      displayName: decodedString?.apay_display_name || envVar.aPayMerchantDisplay,
      gateway: payment_providers_names.CARDSTREAM,
      cardstreamMerchantId: String(decodedString?.cs_merchant_id),
    });
  };

  const onRequestApplePay = useCallback(() => {
    if (!ApplePaySession) return;

    let session;
    try {
      const request = {
        countryCode: decodedString?.country_code,
        currencyCode: decodedString?.currency_name,
        merchantCapabilities: ['supports3DS'],
        supportedNetworks,
        total: {
          label: envVar.aPayMerchantDisplay,
          amount: parseFloat(decodedString?.total).toFixed(2),
        },
      };

      session = new ApplePaySession(envVar.aPayVersionNumber, request);
      session.begin();

      session.onvalidatemerchant = async (event) => {
        try {
          const res = await validateMerchant(event.validationURL);
          session.completeMerchantValidation(res.data?.data);
        } catch (error) {
          console.error('ApplePayCS merchant validation error', error);
          session.completePayment(ApplePaySession.STATUS_FAILURE);
          setLoading(false);
          setMessage(`${errerMessages.CANNOT_PROCESS_PAYMENT} #APCS707`);
          setFailedMessage(errerMessages.SOMETHING_WENT_WRONG);
          setError(true);
        }
      };

      session.onpaymentauthorized = async (event) => {
        try {
          setLoading(true);

          const response = await axios.post(
            `${baseUrl.earth}/sale/digital-wallet?via=${VIA_PARAM}`,
            {
              data: encryptData,
              token: event.payment?.token?.paymentData,
              base64Data,
              kountSessionId: localStorage.kSessionId,
              origin: 'datman_v1',
              deviceInfo: deviceInfo.GetDeviceInfo(),
            }
          );

          // success
          if (response.status === 200) {
            session.completePayment(ApplePaySession.STATUS_SUCCESS);
            const redirectUrl = response.data?.data?.redirectUrl || decodedString?.redirect_url;
            redirectUrl && window.location.replace(redirectUrl);
            return;
          }

          // 3DS required
          if (response.status === 201) {
            const { success, threeDSreq, order_id } = response.data.data;
            if (success === '3dsV2' && threeDSreq?.hasOwnProperty('threeDSMethodData')) {
              session.completePayment(ApplePaySession.STATUS_SUCCESS);
              await threeDsRedirect.threeDsV2AcsRedirect(threeDSreq, { orderId: order_id });
            } else if (success === '3dsV2' && threeDSreq?.hasOwnProperty('creq')) {
              session.completePayment(ApplePaySession.STATUS_SUCCESS);
              threeDsRedirect.AcsRedirect({
                action: 'creq',
                threeDSURL: threeDSreq.threeDSURL,
                creq: threeDSreq.creq,
              });
            } else {
              session.completePayment(ApplePaySession.STATUS_FAILURE);
              setLoading(false);
              setMessage(`${errerMessages.CANNOT_PROCESS_PAYMENT} #APCS710`);
              setFailedMessage(errerMessages.SOMETHING_WENT_WRONG);
              setError(true);
            }
            return;
          }

          session.completePayment(ApplePaySession.STATUS_FAILURE);
          setLoading(false);
          setMessage(`${errerMessages.CANNOT_PROCESS_PAYMENT} #APCS705`);
          setFailedMessage(errerMessages.SOMETHING_WENT_WRONG);
          setError(true);
        } catch (error) {
          console.error('ApplePayCS payment error', error);
          session.completePayment(ApplePaySession.STATUS_FAILURE);
          setLoading(false);
          setError(true);
          if (error?.response?.status === 422) {
            setFailedMessage(error?.response?.data?.message || errerMessages.SOMETHING_WENT_WRONG);
          } else if (error?.response?.status === 402) {
            setMessage(`${errerMessages.KOUNT_FAILED_ERROR} #APCS708KT`);
            setFailedMessage(errerMessages.PAYMENT_DECLINED_FOR_SECURITY_REASONS);
          } else {
            setMessage(`${errerMessages.CANNOT_PROCESS_PAYMENT} #APCS706`);
            setFailedMessage(errerMessages.SOMETHING_WENT_WRONG);
          }
        }
      };

      session.oncancel = () => {
        setLoading(false);
      };
    } catch (error) {
      console.error('ApplePayCS session error', error);
      if (session) session.completePayment(ApplePaySession.STATUS_FAILURE);
      setLoading(false);
      setMessage(`${errerMessages.CANNOT_PROCESS_PAYMENT} #APCS709`);
      setFailedMessage(errerMessages.SOMETHING_WENT_WRONG);
      setError(true);
    }
  }, [ApplePaySession, decodedString, supportedNetworks, encryptData, base64Data, setLoading, setMessage, setFailedMessage, setError]);

  useEffect(() => {
    const el = document.querySelector('apple-pay-button');
    if (!el) return;
    el.addEventListener('click', onRequestApplePay);
    return () => el.removeEventListener('click', onRequestApplePay);
  }, [onRequestApplePay]);

  return (
    <div className='apple-pay-button-container' style={{ display: isEnabled ? 'block' : 'none' }}>
      <apple-pay-button style={{ display: 'inherit' }} buttonstyle='black' type='pay' locale='en-GB'></apple-pay-button>
    </div>
  );
}
